import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-12efd36b"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "page-content-section__title-wrap" }
const _hoisted_3 = { class: "page-content-section__main" }
const _hoisted_4 = {
  key: 0,
  class: "page-content-section__illustration"
}
const _hoisted_5 = {
  key: 1,
  class: "page-content-section__footer"
}

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  provide,
  ref,
  useSlots,
  watch,
} from "vue";
import IconChevronDown from "@/icons/IconChevronDown.vue";
import {
  createSectionId,
  usePageSectionNav,
} from "@/layout/_shared/usePageSectionNav";

/**
 * PageContentSection
 *
 * White card section used inside `PageContentBase` with `plain`,
 * or as a nested section within detail pages.
 * When `#illustration` is provided, body uses a 4-4-4 grid:
 * two content columns + Lottie in the last column.
 * `#footer` spans the full width below that grid as a separate row.
 *
 * When inside a page-section navigator context and not hidden from nav,
 * registers itself for the floating right-side Detail navigator.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'PageContentSection',
  props: {
  alwaysExpanded: {
    type: Boolean,
    default: false,
  },
  defaultExpanded: {
    type: Boolean,
    default: true,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  /** Optional explicit nav label; defaults to header text. */
  navLabel: {
    type: String,
    default: "",
  },
  /** Skip floating section navigator registration. */
  hideFromNav: {
    type: Boolean,
    default: false,
  },
  /** Optional stable DOM / nav id. */
  sectionId: {
    type: String,
    default: "",
  },
},
  setup(__props, { expose: __expose }) {

const props = __props;

const slots = useSlots();
const hasIllustration = computed(() => Boolean(slots.illustration));
const hasFooter = computed(() => Boolean(slots.footer));

provide("pageContentSectionHasIllustration", hasIllustration);

const isExpanded = ref(props.defaultExpanded);
const rootEl = ref<HTMLElement | null>(null);
const headerEl = ref<HTMLElement | null>(null);
const titleEl = ref<HTMLElement | null>(null);
const sectionDomId = props.sectionId || createSectionId();
const sectionNav = usePageSectionNav();
const registered = ref(false);

const toggle = () => {
  if (!props.alwaysExpanded) {
    isExpanded.value = !isExpanded.value;
  }
};

const expand = () => {
  isExpanded.value = true;
};

const handleHeaderClick = (event: Event) => {
  if (props.alwaysExpanded) {
    return;
  }

  const target = event.target as HTMLElement;
  if (target.closest(".page-content-section__actions")) {
    return;
  }

  toggle();
};

const resolveNavLabel = (): string => {
  if (props.navLabel.trim()) {
    return props.navLabel.trim();
  }

  return titleEl.value?.textContent?.replace(/\s+/g, " ").trim() || "";
};

let lastRegisteredKey = "";

const syncNavRegistration = () => {
  if (
    !sectionNav ||
    !rootEl.value ||
    !headerEl.value ||
    props.hideFromNav ||
    props.hideHeader
  ) {
    if (registered.value && sectionNav) {
      sectionNav.unregister(sectionDomId);
      registered.value = false;
      lastRegisteredKey = "";
    }
    return;
  }

  const label = resolveNavLabel();
  if (!label) {
    if (registered.value) {
      sectionNav.unregister(sectionDomId);
      registered.value = false;
      lastRegisteredKey = "";
    }
    return;
  }

  const payload = {
    id: sectionDomId,
    label,
    el: rootEl.value,
    headerEl: headerEl.value,
    expand,
  };

  const nextKey = `${label}::${sectionDomId}`;

  if (registered.value) {
    if (lastRegisteredKey === nextKey) {
      return;
    }
    sectionNav.update(sectionDomId, payload);
  } else {
    sectionNav.register(payload);
    registered.value = true;
  }

  lastRegisteredKey = nextKey;
};

onMounted(() => {
  nextTick(syncNavRegistration);
});

onUpdated(() => {
  nextTick(syncNavRegistration);
});

watch(
  () => [props.navLabel, props.hideFromNav, props.hideHeader],
  () => {
    nextTick(syncNavRegistration);
  }
);

onBeforeUnmount(() => {
  if (registered.value && sectionNav) {
    sectionNav.unregister(sectionDomId);
    registered.value = false;
  }
});

__expose({ toggle, expand });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: _unref(sectionDomId),
    ref_key: "rootEl",
    ref: rootEl,
    class: "page-content-section"
  }, [
    (!__props.hideHeader)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "headerEl",
          ref: headerEl,
          class: _normalizeClass(["page-content-section__header", { 'page-content-section__header--toggle': !__props.alwaysExpanded }]),
          onClick: handleHeaderClick
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", {
              class: _normalizeClass(["page-content-section__toggle", {
            'page-content-section__toggle--open': isExpanded.value || __props.alwaysExpanded,
            'page-content-section__toggle--static': __props.alwaysExpanded,
          }]),
              "aria-hidden": "true"
            }, [
              _createVNode(IconChevronDown, { class: "page-content-section__caret" })
            ], 2),
            _createElementVNode("h3", {
              ref_key: "titleEl",
              ref: titleEl,
              class: "page-content-section__title"
            }, [
              _renderSlot(_ctx.$slots, "header")
            ], 512)
          ]),
          _createElementVNode("div", {
            class: "page-content-section__actions",
            onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
          }, [
            _renderSlot(_ctx.$slots, "actions")
          ])
        ], 2))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", {
      class: _normalizeClass(["page-content-section__body", {
        'page-content-section__body--with-illustration': hasIllustration.value,
        'page-content-section__body--with-footer': hasFooter.value,
      }])
    }, [
      _createElementVNode("div", _hoisted_3, [
        _renderSlot(_ctx.$slots, "default")
      ]),
      (hasIllustration.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _renderSlot(_ctx.$slots, "illustration")
          ]))
        : _createCommentVNode("", true),
      (hasFooter.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _renderSlot(_ctx.$slots, "footer")
          ]))
        : _createCommentVNode("", true)
    ], 2), [
      [_vShow, __props.hideHeader || isExpanded.value]
    ])
  ], 8, _hoisted_1))
}
}

})